const numberFormatter = new Intl.NumberFormat();

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

export const formatCount = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? numberFormatter.format(value)
    : '0';

export const formatCurrency = (value: number | null | undefined): string =>
  currencyFormatter.format(
    typeof value === 'number' && Number.isFinite(value) ? value : 0,
  );

const parse = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateTime = (value: string | null | undefined): string => {
  const parsed = parse(value);
  return parsed ? dateTimeFormatter.format(parsed) : '';
};

export const formatDate = (value: string | null | undefined): string => {
  const parsed = parse(value);
  return parsed ? dateFormatter.format(parsed) : '';
};

/** `yyyy-mm-dd` for `<input type="date">`, in the operator's local time. */
export const toDateInputValue = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const daysAgoInputValue = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInputValue(date);
};

/**
 * `<input type="date">` can be cleared to `''`, and partially-typed values are
 * reported too, so these must stay total: an unparseable day yields `''` rather
 * than throwing out of the caller's render.
 */
const dayToIso = (value: string, time: string): string => {
  const parsed = parse(`${value}T${time}`);
  return parsed ? parsed.toISOString() : '';
};

/** Start of the given `yyyy-mm-dd` day as an ISO instant, `''` if unparseable. */
export const dateInputToIsoStart = (value: string): string =>
  dayToIso(value, '00:00:00');

/** End of the given `yyyy-mm-dd` day as an ISO instant, `''` if unparseable. */
export const dateInputToIsoEnd = (value: string): string =>
  dayToIso(value, '23:59:59');

/** True when both bounds parse and the range is not inverted. */
export const isValidDateRange = (start: string, end: string): boolean => {
  const from = parse(`${start}T00:00:00`);
  const to = parse(`${end}T23:59:59`);
  return Boolean(from && to && from.getTime() <= to.getTime());
};
